import { createElement, useCallback, useRef, useState } from "react";
import AppNoticeDialog from "./AppNoticeDialog";

/**
 * Promise-based replacement for window.alert / window.confirm.
 *
 *   const { dialog, alert, confirm } = useAppNotice(isDarkMode);
 *   await alert({ title, message, variant: "error" });
 *   const ok = await confirm({ title, message, variant: "danger" });
 */
export default function useAppNotice(isDarkMode = false) {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);

  const settle = useCallback((result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    resolve?.(result);
  }, []);

  const open = useCallback((options) => {
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ mode: "alert", variant: "info", ...options });
    });
  }, []);

  const alert = useCallback(
    (options) => open({ mode: "alert", variant: options?.variant || "info", ...options }),
    [open]
  );

  const confirm = useCallback(
    (options) => open({ mode: "confirm", variant: options?.variant || "warning", ...options }),
    [open]
  );

  const dialog = createElement(AppNoticeDialog, {
    open: Boolean(state),
    isDarkMode,
    ...(state || {}),
    onConfirm: () => settle(true),
    onCancel: () => settle(false),
  });

  return { dialog, alert, confirm };
}
